import $ from 'jquery';
import product from '@wc-stripe/product';
import BaseController from './BaseController';
import {convertFormToFields} from "../helpers/utils";

/**
 * Product Controller
 *
 * Handles WooCommerce product page payment flow orchestration.
 * Works with any payment gateway (Link Express, Apple Pay, Google Pay, etc.)
 *
 * @since 4.0.0
 */
class ProductController extends BaseController {

    _cartItemKey;

    _excludedKeys = ['add-to-cart', 'quantity', 'variation_id'];

    constructor(props) {
        super(props);
        this.product = product;
    }

    initialize() {
        this.actions.addAction('product_updated', this.onProductUpdated.bind(this));
        this.actions.addAction('cart_updated', this.onCartUpdated.bind(this));
        this.actions.addAction('before_cart_calculation', this.onBeforeCartCalculation.bind(this));
        this.actions.addAction('after_cart_calculation', this.onAfterCartCalculation.bind(this));
        this.actions.addAction('removed_from_cart', this.onRemovedFromCart.bind(this), 20);
        this.actions.addAction('before_remove_cart_item', this.onBeforeRemoveCartItem.bind(this));
        this.actions.addAction('after_remove_cart_item', this.onAfterRemoveCartItem.bind(this));
        this.actions.addFilter('cart_calculation_enabled', this.onCartCalculationEnabled.bind(this));
        super.initialize();
    }

    onCartCalculationEnabled(enabled) {
        return enabled || this._isPaymentMethodAvailable;
    }

    onPaymentElementReady() {
        super.onPaymentElementReady();
        if (this.product.isVariableProduct()) {
            if (!this.product.isVariationSelected() || !this.product.isInStock() || this.product.getTotalCents() <= 0) {
                this.disablePaymentElement(true);
            } else {
                this.enablePaymentElement();
            }
        }
        $(`li.payment_method_${this.gateway.id}`).show();
        $('.wc-stripe-product-checkout-container').addClass('active');
    }

    onPaymentUnavailable() {
        super.onPaymentUnavailable();
        $(`li.payment_method_${this.gateway.id}`).hide();
    }

    needsShipping() {
        // If the cart is empty or the cart doesn't need shipping already,
        // defer to the product's shipping.
        if (this.cart.isEmpty() || !this.cart.needsShipping()) {
            return this.product.needsShipping();
        }
        return this.cart.needsShipping();
    }

    getElementContainer() {
        let el = document.querySelector(this.selector);
        if (!el) {
            // Build list of selectors to try: new pattern first, then legacy patterns
            // New pattern: .wc-stripe_{gateway_id}-{context}
            // Old pattern: .wc-stripe-{gateway_id}-{context} (hyphen instead of underscore)
            const gatewayId = this.gateway.id;
            const gatewayIdHyphenated = gatewayId.replace(/_/g, '-');

            const queries = [
                // New class pattern (underscore after wc-stripe)
                `#wc-${gatewayId}-product-button`,
                `#wc-${gatewayIdHyphenated}-product-button`,
                // Legacy ID patterns with -container suffix
                `#wc-${gatewayId}-product`,
                `#wc-${gatewayId}-container`,
                `#wc-${gatewayIdHyphenated}-container`
            ];

            for (const query of queries) {
                el = document.querySelector(query);
                if (el) {
                    return el;
                }
            }
        }
        return super.getElementContainer();
    }

    submitError(error) {
        this.hideProcessing();
        this.displayErrorMessage(error, 'div.woocommerce-notices-wrapper');
    }

    onClick() {
        super.onClick();
        this.promises.push(this.addToCart());
    }

    /**
     * Add product to cart before creating order
     */
    addToCart() {
        return this.cart.addToCart({
            payment_method: this.gateway.id,
            product_id: this.product.getId(),
            variation_id: this.product.getVariationId(),
            qty: this.product.getQuantity(),
            variation: this.product.getVariationData(),
            ...this.actions.applyFilters('add_to_cart_data', convertFormToFields($('form.cart'), this._excludedKeys), this)
        }).then(response => {
            if (response.code) {
                this.currentError = response;
            } else {
                this._cartItemKey = response.cart_item_key;
                return response.order_id;
            }
        }).catch(async err => {
            this.currentError = err;
            // The payment sheet should be closed so the error message is displayed.
            if (this.gateway.supports('payment_sheet')) {
                this.gateway.closePaymentSheet();
                await this.createPaymentElement();
            }
            return this.submitError(err.message);
        })
    }

    async removeCartItem() {
        if (!this._cartItemKey) {
            return;
        }
        await this.cart.removeCartItem(this._cartItemKey);
    }

    /**
     * Handle any product data change: variation selected/reset, quantity change,
     * or 3rd party updates (e.g. product addon price changes via product.update({price})).
     *
     * @param {Object} product - The product instance
     * @param {Object} prevData - Data snapshot before the update
     */
    async onProductUpdated(product, prevData) {
        // only assign if the current value is falsy
        this._destroyPaymentElement ||= product.needsShipping() !== prevData?.needsShipping;

        if (!product.isInStock() || product.getTotalCents() <= 0) {
            this.disablePaymentElement(true);
            return;
        }

        if (product.isVariableProduct() && !product.isVariationSelected()) {
            this.disablePaymentElement(true);
            return;
        }

        // Physical products don't need a server calculation — the payment sheet handles
        // shipping. Only virtual products need a cart calculation to get accurate totals.
        if (product.needsCartCalculation()) {
            // onBeforeCartCalculation / onAfterCartCalculation handle the rest
            return;
        }

        // No cart calculation needed — update elements in place
        if (this._destroyPaymentElement) {
            await this.destroyPaymentElement();
            this._destroyPaymentElement = null;
            await this.createPaymentElement();
        } else if (!this._doingCartCalculation) {
            this.gateway.updateElements();
            this.enablePaymentElement();
        }
    }

    onBeforeCartCalculation() {
        this._doingCartCalculation = true;
        // Shipping-needing products get a guaranteed correction checkpoint before payment can be
        // confirmed: Apple/Google Pay require a shipping address, which fires onShippingChange
        // (BaseController) - that awaits the pending add-to-cart request and calls
        // cart.updateShipping() to push the accurate total into the sheet before the customer can
        // pay. Digital/non-shipping products skip that step entirely, so the amount set at click
        // time (see AbstractExpressGateway.onClickElement) is the only chance to get it right -
        // the element must stay disabled until the server-computed total is known.
        if (!this.needsShipping()) {
            this.disablePaymentElement(true);
        }
    }

    async onAfterCartCalculation() {
        this._doingCartCalculation = false;
        this.enablePaymentElement(true);

        // If current shipping doesn't match previous shipping then the payment element
        // needs to be re-created.
        if (this.product.isVariableProduct()) {
            if (this._destroyPaymentElement) {
                await this.destroyPaymentElement();
                this._destroyPaymentElement = null;
            }
        }
        await this.createPaymentElement();
    }

    onBeforeRemoveCartItem() {
        this.disablePaymentElement(true);
    }

    onAfterRemoveCartItem() {
        this.enablePaymentElement(true);
    }

    async onRemovedFromCart() {
        await this.destroyPaymentElement();
        await this.createPaymentElement();
    }

    onCartUpdated() {
        if (this.gateway.supports('elements')) {
            this.gateway.updateElements();
        }
    }

    onCancel() {
        $('.single_add_to_cart_button').prop('disabled', true);
        this.removeCartItem().finally(() => {
            $('.single_add_to_cart_button').prop('disabled', false);
            super.onCancel();
        });
    }

    getTotal() {
        if (this.cart.isEmpty()) {
            return this.product.getTotal();
        }
        return super.getTotal();
    }

    getTotalCents() {
        if (this.cart.isEmpty()) {
            return this.product.getTotalCents();
        }
        return super.getTotalCents();
    }

    getLineItems() {
        if (this.cart.isEmpty()) {
            return this.product.getLineItems();
        }
        return super.getLineItems();
    }
}

export {ProductController};
export default ProductController;